import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api.js';
import { useI18n } from './i18n.js';

export type Me = Schemas['Me'];

export type SessionState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'signed-out' }
  | { readonly kind: 'mfa-pending' }
  | { readonly kind: 'signed-in'; readonly me: Me }
  | { readonly kind: 'error'; readonly error: unknown };

interface SessionContextValue {
  readonly state: SessionState;
  readonly refresh: () => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { readonly children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ kind: 'loading' });
  const { setLocale } = useI18n();

  const refresh = useCallback(async () => {
    try {
      const me = await unwrap(api.GET('/admin-api/v1/me'));
      setState({ kind: 'signed-in', me });
      setLocale(me.user.preferredLocale);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'unauthenticated')
        setState({ kind: 'signed-out' });
      else if (error instanceof ApiError && error.code === 'mfa-required')
        setState({ kind: 'mfa-pending' });
      else setState({ kind: 'error', error });
    }
  }, [setLocale]);

  const signOut = useCallback(async () => {
    try {
      await unwrap(api.POST('/admin-api/v1/auth/logout'));
    } finally {
      setState({ kind: 'signed-out' });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <SessionContext.Provider value={{ state, refresh, signOut }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (context === null) throw new Error('useSession outside SessionProvider');
  return context;
}

/** The signed-in account (only call under a route that requires a session). */
export function useMe(): Me {
  const { state } = useSession();
  if (state.kind !== 'signed-in') throw new Error('useMe without a signed-in session');
  return state.me;
}
